import { bytesEqual, copyBytes, decodeBase64URL, encodeBase64URL } from "./bytes.js";
import type { OwnedBytes } from "./bytes.js";

const bindIDBytes = 32;

export class BindId {
  readonly #bytes: OwnedBytes;

  private constructor(bytes: OwnedBytes) {
    this.#bytes = bytes;
  }

  static parse(value: string): BindId {
    return BindId.fromBytes(decodeBase64URL(value));
  }

  static fromBytes(value: BufferSource): BindId {
    const bytes = copyBytes(value);
    if (bytes.byteLength !== bindIDBytes) throw new Error("Bind ID must contain exactly 32 bytes");
    return new BindId(bytes);
  }

  static async fromPublicKey(publicKeySPKI: BufferSource): Promise<BindId> {
    return BindId.fromBytes(await crypto.subtle.digest("SHA-256", copyBytes(publicKeySPKI)));
  }

  toBytes(): OwnedBytes {
    return this.#bytes.slice();
  }

  toString(): string {
    return encodeBase64URL(this.#bytes);
  }

  equals(other: BindId): boolean {
    return bytesEqual(this.#bytes, other.#bytes);
  }
}

export interface Endpoint {
  relayUrl: string;
  bindId: BindId;
  basePath?: string;
}

export type RelaySocketKind = "bind" | "connect" | "session";

export function relayWebSocketURL(
  endpoint: Pick<Endpoint, "relayUrl" | "basePath">,
  kind: RelaySocketKind,
  identifier: string,
): string {
  const url = new URL(endpoint.relayUrl);
  if (url.protocol !== "https:" && url.protocol !== "http:" && url.protocol !== "wss:" && url.protocol !== "ws:") {
    throw new Error("Relay URL must use HTTPS, HTTP, WSS, or WS");
  }
  if (url.username || url.password || url.search || url.hash || (url.pathname !== "" && url.pathname !== "/")) {
    throw new Error("Relay URL must be an origin without credentials, query, fragment, or path");
  }
  const basePath = endpoint.basePath ?? "/v1";
  if (!basePath.startsWith("/") || basePath === "/" || basePath.endsWith("/")) {
    throw new Error("Relay base path must begin with / and have no trailing /");
  }
  url.protocol = url.protocol === "https:" || url.protocol === "wss:" ? "wss:" : "ws:";
  url.pathname = `${basePath}/${kind}/${encodeURIComponent(identifier)}`;
  return url.toString();
}
